/** Phase 2B — Asset picker modal (multi-select). */
import { useEffect, useMemo, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogClose,
} from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Checkbox } from "../../components/ui/checkbox";

const KINDS = ["image", "video", "copy", "prompt", "document", "audio"];

export default function AssetPicker({
  open, onOpenChange, value = [], onChange, max = 50,
}) {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [kind, setKind] = useState("all");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set(value));

  useEffect(() => { setSelected(new Set(value)); }, [value, open]);

  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 100 };
      if (q) params.q = q;
      if (kind !== "all") params.kind = kind;
      const r = await api.get("/assets", { params });
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { if (open) load(); /* eslint-disable-next-line */ }, [open, q, kind]);

  const toggle = (id) => {
    const s = new Set(selected);
    if (s.has(id)) s.delete(id);
    else if (s.size < max) s.add(id);
    setSelected(s);
  };

  const confirm = () => {
    onChange?.(Array.from(selected));
    onOpenChange(false);
  };

  const count = selected.size;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="asset-picker" className="max-w-3xl">
        <DialogHeader>
          <DialogTitle>Pick assets <Badge variant="outline" data-testid="picker-count">{count}</Badge></DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-2 mb-3">
          <Input data-testid="picker-search" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} />
          <Select value={kind} onValueChange={setKind}>
            <SelectTrigger data-testid="picker-kind"><SelectValue placeholder="Kind" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All kinds</SelectItem>
              {KINDS.map((k) => <SelectItem key={k} value={k}>{k}</SelectItem>)}
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={load}>Refresh</Button>
        </div>
        <div className="max-h-[420px] overflow-y-auto border rounded-md divide-y">
          {loading && <div className="p-4 text-sm text-muted-foreground">Loading…</div>}
          {!loading && items.length === 0 && (
            <div className="p-6 text-sm text-muted-foreground text-center">No assets match.</div>
          )}
          {items.map((a) => {
            const checked = selected.has(a.id);
            return (
              <button
                key={a.id}
                type="button"
                onClick={() => toggle(a.id)}
                data-testid={`picker-row-${a.id}`}
                className={[
                  "w-full flex items-center gap-3 p-3 text-left text-sm",
                  checked ? "bg-accent/40" : "hover:bg-accent/30",
                ].join(" ")}
              >
                <Checkbox checked={checked} onCheckedChange={() => toggle(a.id)} />
                {a.kind === "image" && a.preview_url ? (
                  <img src={a.preview_url} alt="" className="size-10 rounded object-cover border" />
                ) : (
                  <div className="size-10 grid place-items-center rounded border bg-muted/40 text-xs font-mono">
                    {a.kind.slice(0, 3)}
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium">{a.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {a.kind} · {a.status} · {(a.tags || []).slice(0, 3).join(", ")}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
        <DialogFooter>
          <DialogClose asChild><Button variant="ghost">Abbrechen</Button></DialogClose>
          <Button onClick={confirm} data-testid="picker-confirm">Attach {count}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
